import { useCallback, useEffect, useState } from 'react';
import { Trophy, Download, MessageCircle, Store as StoreIcon, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { fmtStamp } from './LiveClock';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const PERIODS = [{ days: 30, label: '30 days' }, { days: 90, label: '90 days' }, { days: 0, label: 'All time' }];

const thanksUrl = (r) => {
  const digits = String(r.phone || '').replace(/\D/g, '');
  const to = digits.length === 10 ? `91${digits}` : digits;
  const text = [
    `Namaste ${r.name}!`,
    `Thank you for being one of our most loyal BharatCart customers. You have placed ${r.orders} order${r.orders === 1 ? '' : 's'}${r.favourite_shop?.name ? `, mostly from ${r.favourite_shop.name}` : ''}.`,
    'We are grateful for your support of local shops. — Team BharatCart',
  ].join('\n');
  return `https://wa.me/${to}?text=${encodeURIComponent(text)}`;
};

export default function CustomerInsights() {
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState('spend');
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/customer-insights', { params: { days, sort } }).then((r) => setData(r.data))
      .catch(() => toast.error('Could not load customer insights'));
  }, [days, sort]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const r = await api.get('/admin/customer-insights.csv', { params: { days, sort }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `top-customers-${days ? `${days}d` : 'all-time'}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div data-testid="customer-insights" className="mt-6 card-soft p-5">
      <div className="flex items-center gap-2 flex-wrap">
        <Trophy className="w-5 h-5 text-[#D35400]"/>
        <div className="font-heading font-bold text-lg">Top customers</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border-2 border-ink/15 overflow-hidden" role="group">
            {PERIODS.map((p) => (
              <button key={p.days} data-testid={`insights-period-${p.days || 'all'}`} onClick={() => setDays(p.days)}
                className={`px-3 h-9 text-xs font-bold ${days === p.days ? 'bg-ink text-white' : 'bg-white text-ink/70 hover:bg-muted'}`}>{p.label}</button>
            ))}
          </div>
          <select data-testid="insights-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="border-2 border-ink/15 rounded-lg px-2 h-9 text-xs font-bold bg-white">
            <option value="spend">By spend</option>
            <option value="orders">By orders</option>
          </select>
          <button data-testid="insights-csv" onClick={downloadCsv} className="btn-outline !py-2 !px-3 text-xs"><Download className="w-3.5 h-3.5"/>CSV</button>
        </div>
      </div>
      {!data ? <div className="mt-4 text-sm text-ink/60">Loading…</div> : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Paying customers" value={data.summary.customers} testid="insights-sum-customers"/>
            <Stat label="Orders" value={data.summary.orders} testid="insights-sum-orders"/>
            <Stat label="Total spend" value={inr(data.summary.spend)} testid="insights-sum-spend"/>
            <Stat label={`Top ${data.rows.length} share`} value={`${data.summary.top_share_pct}%`} testid="insights-sum-share"/>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink/60 border-b border-ink/10">
                <th className="py-2 pr-2">#</th><th className="pr-2">Customer</th><th className="pr-2 text-right">Orders</th>
                <th className="pr-2 text-right">Spend</th><th className="pr-2 text-right">Avg order</th><th className="pr-2">Last order</th>
                <th className="pr-2">Favourite shop</th><th/>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => <InsightRow key={r.user_id} r={r}/>)}
              </tbody>
            </table>
            {!data.rows.length && <div data-testid="insights-empty" className="text-center text-sm text-ink/60 py-8">No orders in this period yet.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function InsightRow({ r }) {
  const medal = ['#C9A227', '#9E9E9E', '#A0522D'][r.rank - 1];
  return (
    <tr data-testid={`insights-row-${r.rank}`} className="border-b border-ink/5 align-top">
      <td className="py-2.5 pr-2">
        <span className="w-7 h-7 rounded-full grid place-items-center text-xs font-black text-white" style={{ background: medal || '#455A64' }}>{r.rank}</span>
      </td>
      <td className="pr-2 py-2.5">
        <div className="font-bold">{r.name}</div>
        <div className="text-[11px] text-ink/60">{r.phone}{r.serial_id ? ` · ${r.serial_id}` : ''}</div>
        {r.village && <div className="text-[11px] text-ink/60 flex items-center gap-1"><MapPin className="w-3 h-3"/>{r.village}</div>}
      </td>
      <td className="pr-2 py-2.5 text-right tabular-nums font-bold" data-testid={`insights-orders-${r.rank}`}>{r.orders}</td>
      <td className="pr-2 py-2.5 text-right tabular-nums font-black" data-testid={`insights-spend-${r.rank}`}>{inr(r.spend)}</td>
      <td className="pr-2 py-2.5 text-right tabular-nums">{inr(r.avg_order)}</td>
      <td className="pr-2 py-2.5 text-xs whitespace-nowrap">{fmtStamp(r.last_order_at)}</td>
      <td className="pr-2 py-2.5 text-xs">
        {r.favourite_shop?.name && <span className="inline-flex items-center gap-1"><StoreIcon className="w-3 h-3"/>{r.favourite_shop.name} <span className="text-ink/50">({r.favourite_shop.orders})</span></span>}
      </td>
      <td className="py-2.5">
        {r.phone && (
          <a data-testid={`insights-thanks-${r.rank}`} href={thanksUrl(r)} target="_blank" rel="noreferrer"
            className="pill text-xs !bg-[#25D366] !text-white !border-[#25D366] whitespace-nowrap"><MessageCircle className="w-3.5 h-3.5"/>Thank</a>
        )}
      </td>
    </tr>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-white border-2 border-ink/10 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">{label}</div>
      <div data-testid={testid} className="font-heading font-black text-xl tabular-nums">{value}</div>
    </div>
  );
}
